import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "@environments/environment";
import { ApiResponse } from "@app/_models";
import { Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class UserService {
  private _baseUrl: string;
  // Remove once webcluster is moved
  // private webclusterUrl: string = "https://relay.corp.adobe.com/relay-webcluster/";

  constructor(private http: HttpClient) {
    this._baseUrl = environment.apiUrl;
  }

  getInboxList(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}operations/inbox`);
  }

  getWebLocatorList(searchParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}webLocators/list`, searchParams);
  }

  getWebLocatorById(webLocId): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}webLocators/${webLocId}`);
  }

  addWebLocator(searchParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}webLocators`, searchParams);
  }
  addPlaywrightWebLocator(searchParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}pwWebLocators`, searchParams);
  }

  updateWebLocator(searchParams): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}webLocators`, searchParams);
  }
  updatePlaywrightWebLocator(searchParams): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}pwWebLocators`, searchParams);
  }

  deleteWebLocator(webLocId): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}webLocators/${webLocId}`);
  }
  deletePlaywrightWebLocator(webLocId): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}pwWebLocators/${webLocId}`);
  }

  getScheduledExecList(searchParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}scheduledExecutions/byOwner`, searchParams);
  }

  getNotifications(searchParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}users/notifications`, searchParams);
  }

  associateUser(selectedUser): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}projectUserRole/associate`, selectedUser);
  }

  deassociateUser(selectedUser): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}projectUserRole/deassociate`, selectedUser);
  }

  getProjectList(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}projects`);
  }

  getProjectListLazy(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}projects/lazy`);
  }

  getMyProjectList(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}users/myProjects`);
  }

  getAssociatedProjects(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}projectUserRole/user`);
  }

  getProjectUserRoleByProject(id): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}projectUserRole/project/${id}`);
  }

  getMaskableFields(id): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}projects/${id}/maskableFields`);
  }

  updateMaskableFields(id, maskableFieldList): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}projects/${id}/maskableFields`, maskableFieldList);
  }

  getEnvironmentList(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}environments`);
  }

  getEnvironmentPageList(searchParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}environments/readAll`, searchParams);
  }

  getEnvironmentMaps(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}environmentMaps`);
  }

  getEnvironmentMapsByPage(searchParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}environmentMaps/readAll`, searchParams);
  }

  addEnvironment(searchParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}environments`, searchParams);
  }

  getEnvironmentMapsById(id) {
    return this.http.get<ApiResponse>(`${this._baseUrl}environments/environmentMap/${id}`);
  }

  addEnvironmentMapper(requestParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}environmentMaps`, requestParams);
  }

  editEnvironment(searchParams): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}environments`, searchParams);
  }

  updateEnvironmentMapper(requestParams): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}environmentMaps`, requestParams);
  }

  deleteEnvironment(id): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}environments/${id}`);
  }

  deleteEnvironmentMapper(id): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}environmentMaps/${id}`);
  }
  ForceDeleteEnvironmentMapper(id): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}environmentMaps/${id}?forceDelete=true`);
  }

  getFilesUploadedForProject(id, paramFile = false, fileType?): Observable<ApiResponse> {
    const url = fileType ? '&fileTypes=' + fileType : '';
    return this.http.get<ApiResponse>(`${this._baseUrl}file/${id}?paramFile=${paramFile}` + url);
  }

  getFiles(searchParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}file/list`, searchParams);
  }

  getFilesByProject(projectId: number) {
    return this.http.get<any>(`${environment.apiUrl}file/${projectId}`);
  }

  uploadFile(projectId, file, replacePlaceHolders, paramFile, bulkUpload, validation, fileName?): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      `${this._baseUrl}file/${projectId}?replacePlaceHolders=${replacePlaceHolders}&paramFile=${paramFile}&bulkUpload=${bulkUpload}&validation=${validation}&fileName=${fileName}`,
      file
    );
  }

  editFile(projectId, file, replacePlaceHolders, fileInfoId, paramFile, bulkUpload, validation, fileName?): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      `${this._baseUrl}file/${projectId}?replacePlaceHolders=${replacePlaceHolders}&fileInfoId=${fileInfoId}&paramFile=${paramFile}&bulkUpload=${bulkUpload}&validation=${validation}&fileName=${fileName}`,
      file
    );
  }

  deleteFile(fileId): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}file/${fileId}`);
  }

  getGloabalTestInputKeysById(projectId): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}globalTestInputKey?projectId=${projectId}`);
  }

  getGloabalTestInputValuesById(projectId): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}globalTestInputValue?projectId=${projectId}`);
  }

  getEnvirontmentMapsByProjectId(projectId): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}environmentMaps/projects/${projectId}`);
  }

  getTestcasesWithGlobalExtractors(projectId): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}testCases/list/storeInGlobalExtractors/project/${projectId}`);
  }

  getEndpointsWithGlobalExtractors(testCaseId): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}testCases/${testCaseId}/storeInGlobalExtractors`);
  }

  getGlobalExtractorsByEndpointId(endpointId): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}testCases/testCaseEndPoint/${endpointId}/storeInGlobalExtractors`);
  }

  getSecretFieldsByProjectId(projectId): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}secretFields/projects/${projectId}`);
  }

  addGlobalTestInputKey(requestParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}globalTestInputKey`, requestParams);
  }

  updateGlobalTestInputKey(requestParams): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}globalTestInputKey`, requestParams);
  }

  addGlobalTestInputValue(requestParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}globalTestInputValue`, requestParams);
  }

  updateGlobalTestInputValue(requestParams): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}globalTestInputValue`, requestParams);
  }

  deleteGlobalTestInputKey(testInputKeyId): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}globalTestInputKey/${testInputKeyId}`);
  }

  exportGlobalTestData(projectId: any) {
    return this.http.get<ApiResponse>(`${this._baseUrl}globalTestInputKey/exportToFile/${projectId}`);
  }

  exportInputData(requestObject: any) {
    return this.http.get<ApiResponse>(`${this._baseUrl}globalTestInputFileValue/generateExcel/${requestObject.projectId}/${requestObject.environmentMapId}?ignoreBlankData=${requestObject.ignoreBlankData}`);
  }

  deleteGlobalTestInputValue(testInputValueId): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}globalTestInputValue/${testInputValueId}`);
  }

  /* file Upload APIs for Global Test Data */
  getUploadedFilesForGlobalTestData(projectId): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}globalTestInputFileValue?projectId=${projectId}`)
  }

  addFileForGlobalTestData(fileInfo: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}globalTestInputFileValue`, fileInfo)
  }

  updateFileForGlobalTestData(fileInfo: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}globalTestInputFileValue`, fileInfo)
  }

  deleteFileForGlobalTestData(fileId: any): Observable<ApiResponse>{
    return this.http.delete<ApiResponse>(`${this._baseUrl}globalTestInputFileValue/${fileId}`)
  }

  /*for  Modified GlobalTest data page */

  getInputValuesByInputKeyId(globalTestInputKeyId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}globalTestInputValue?globalTestInputKeyId=${globalTestInputKeyId}`);
  }

  updateInputValueWithMultipleEnvironments(requestParams: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}globalTestInputValue/updateMultiple`, requestParams);
  }

  getImsList(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}users/ims`);
  }

  addIms(requestParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}users/ims`, requestParams);
  }

  updateIms(requestParams): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}users/ims?id=${requestParams.id}`, requestParams);
  }

  deleteImsById(imsId): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}users/ims/${imsId}`);
  }

  getCompanies(): Observable<any> {
    return this.http.get<any>(`${this._baseUrl}companies`);
  }

  getUsers(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}users`);
  }
  serviceId(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}serviceId`);
  }

  getAllServices(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}services`);
  }

  importEnvironments(formData, projectId): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}environments/import/projects/${projectId}`, formData);
  }

  importEnvironmentMaps(formData, projectId): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}environmentMaps/import/projects/${projectId}`, formData);
  }

  importSwaggerApi(projectId, swaggerConfig): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}projects/swagger/import/v1/${projectId}`, swaggerConfig);
  }

  setHierarchy(id): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}projects/${id}/hierarchy`);
  }

  getLdapUser(searchText): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}users/ldap/search/${searchText}`);
  }

  getOrgainizationsByCompany(companyId: number): Observable<ApiResponse> {
    return this.http.get<any>(`${this._baseUrl}companies/${companyId}/children`);
  }

  getGroupsByOrganisation(orgId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}organizations/${orgId}/children`);
  }

  getPortfoliosByGroup(groupId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}groups/${groupId}/children`);
  }

  getServicesByPortfolio(portfolioId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}portfolios/${portfolioId}/children`);
  }

  getProjectsByService(serviceId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}services/${serviceId}/children`);
  }

  submitComment(userId: number, comment: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}users/${userId}/comment`, comment);
  }

  getScheduledTriggers(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}scheduledExecutions/scheduledTriggers`);
  }

  addCompany(requestParams: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}companies`, requestParams);
  }

  addOrganization(requestParams: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}organizations`, requestParams);
  }

  addGroup(requestParams: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}groups`, requestParams);
  }

  addPortfolio(requestParams: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}portfolios`, requestParams);
  }

  addService(requestParams: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}services`, requestParams);
  }

  addProject(requestParams: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}projects`, requestParams);
  }

  updateCompany(requestParams: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}companies`, requestParams);
  }

  updateOrganization(requestParams: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}organizations`, requestParams);
  }

  updateGroup(requestParams: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}groups`, requestParams);
  }

  updatePortfolio(requestParams: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}portfolios`, requestParams);
  }

  updateService(requestParams: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}services`, requestParams);
  }

  updateProject(requestParams: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}projects`, requestParams);
  }

  deleteCompany(companyId: number): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}companies/${companyId}`);
  }

  deleteOrganization(orgId: number): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}organizations/${orgId}`);
  }

  deleteGroup(groupId: number): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}groups/${groupId}`);
  }

  deletePortfolio(portfolioId: number): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}portfolios/${portfolioId}`);
  }

  deleteService(serviceId: number): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}services/${serviceId}`);
  }

  deleteProject(projectId: number): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}projects/${projectId}`);
  }

  getBrowserExtensions(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}browserExtensions`);
  }

  addBrowserExtensions(requestParams: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}browserExtensions`, requestParams);
  }

  updateBrowserExtensions(requestParams: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}browserExtensions`, requestParams);
  }

  deleteBrowserExtension(browserExtensionId: number): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}browserExtensions/${browserExtensionId}`);
  }

  associateOrDeassociateProjectsToExtension(browserExtensionId: number, requestParams: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(
      this._baseUrl + "browserExtensions/" + browserExtensionId + "/projectAssociationDisassociation",
      requestParams
    );
  }

  getServices(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}services/owner`);
  }

  getRestrictProjectCreationByServiceId(id): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}services/${id}/restrictProjectCreation`);
  }

  getAdminProjects(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}projects/admin`);
  }

  getSecretAccountsByServiceId(serviceId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}secretAccounts/services/${serviceId}`);
  }

  getSecretAccountsByProjectId(projectId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}secretAccounts/projects/${projectId}`);
  }

  getEnvironmentsByProjectId(projectId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}environments/projects/${projectId}`);
  }

  addSecretAccount(requestParams: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}secretAccounts`, requestParams);
  }

  updateSecretAccount(requestParams: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}secretAccounts`, requestParams);
  }

  deleteSecretAccount(secretAccountId: number): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}secretAccounts/${secretAccountId}`);
  }

  impersonateSecretAccount(secretAccountId: number): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}secretAccounts/impersonate/add/${secretAccountId}`, {
      secretAccountId: secretAccountId,
    });
  }

  revokeImpersonation(secretAccountId: number): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}secretAccounts/impersonate/remove/${secretAccountId}`, {
      secretAccountId: secretAccountId,
    });
  }

  linkSecretAccount(requestParams: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      this._baseUrl + "secretAccounts/" + requestParams.secretAccountId + "/environment/" + requestParams.environmentId + "/associate",
      requestParams
    );
  }

  delinkSecretAccount(requestParams: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      this._baseUrl + "secretAccounts/" + requestParams.secretAccountId + "/environment/" + requestParams.environmentId + "/deassociate",
      requestParams
    );
  }

  getSecretFieldsByEnvId(environmentId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}secretFields/environments/${environmentId}`);
  }

  addNewSecretField(requestParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this._baseUrl}secretFields`, requestParams);
  }

  saveSecrets(secretFieldId: number, requestParams: any, environmentSync: boolean): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      this._baseUrl + "secretAccountFieldMetadata/secretFieldId/" + secretFieldId + "?sync=" + environmentSync,
      requestParams
    );
  }

  updateSecrets(requestParams: any, environmentSync: boolean): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}secretAccountFieldMetadata?sync=${environmentSync}`, requestParams);
  }

  updateSecretFieldName(id: number, requestParams: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this._baseUrl}secretFields?id=${id}`, requestParams);
  }

  pullVaultSecrets(secretAccountId: number, secretPath: any): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}secretAccounts/secretFields/${secretAccountId}?secretPath=${secretPath}`);
  }

  pullSecrets(secretAccountId: number, secretId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this._baseUrl}secretAccounts/secretFields/${secretAccountId}/${secretId}`);
  }

  deleteSecretField(secretFieldId): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}secretFields/${secretFieldId}`);
  }

  getSecretFieldsFromProjectIdForTestData(projectId: string): Observable<any> {
    return this.http.get<any>(`${this._baseUrl}secretFields/projects/${projectId}`);
  }

  getPerfAdminConfigs(): Observable<any> {
    return this.http.get<any>(`${this._baseUrl}performance/relay-admin/config/get`);
  }

  deletePerfAdminConfig(id: number): Observable<any> {
    return this.http.delete<any>(`${this._baseUrl}performance/relay-admin/config/${id}/delete`);
  }

  createPerfAdminConfig(perfAdminConfig: any): Observable<any> {
    return this.http.post<any>(`${this._baseUrl}performance/relay-admin/config/create`, perfAdminConfig);
  }

  updatePerfAdminConfig(perfAdminConfig: any): Observable<any> {
    return this.http.put<any>(`${this._baseUrl}performance/relay-admin/config/update`, perfAdminConfig);
  }

  // Page-Label APIs
  getAllPageLabels(): Observable<any> {
    return this.http.get<any>(`${this._baseUrl}pageLabels`);
  }
  getAllPwPageLabels(): Observable<any> {
    return this.http.get<any>(`${this._baseUrl}pwPageLabels`);
  }

  getPageLabelList(params: any): Observable<any> {
    return this.http.post<any>(`${this._baseUrl}pageLabels/list`, params);
  }

  getPageLabelListByProject(projectId: any): Observable<any> {
    return this.http.get<any>(`${this._baseUrl}pageLabels/${projectId}`);
  }


  createPageLabel(pageLabel): Observable<any> {
    return this.http.post<any>(`${this._baseUrl}pageLabels`, pageLabel);
  }

  updatePageLabel(updatedPageLabel): Observable<any> {
    return this.http.put<any>(`${this._baseUrl}pageLabels`, updatedPageLabel);
  }

  deletePageLabel(pageLabelId: string) {
    return this.http.delete<any>(`${this._baseUrl}pageLabels/${pageLabelId}`);
  }

  associateWebLocLabel(updatedPageLabel: any): Observable<any> {
    return this.http.put<any>(`${this._baseUrl}pageLabels/associateWebLocators`, updatedPageLabel);
  }

  /* Code coverage */

  getConfigByProjectId(id: number): Observable<any> {
    return this.http.get<any>(`${this._baseUrl}codeCoverage/project/${id}/configs`);
  }

  saveConfig(id: number, data: any): Observable<any> {
    return this.http.post<any>(`${this._baseUrl}codeCoverage/project/${id}/configs`, data);
  }

  deleteConfig(projectId: number, configId: number): Observable<any> {
    return this.http.delete<any>(`${this._baseUrl}codeCoverage/project/${projectId}/configs/${configId}`);
  }

  getMetricsByConfig(projectId: number, configId: number): Observable<any> {
    return this.http.get<any>(`${this._baseUrl}codeCoverage/project/${projectId}/configs/${configId}/metrics`);
  }

  generateConfig(projectId: number, configId: number): Observable<any> {
    return this.http.get<any>(`${this._baseUrl}codeCoverage/project/${projectId}/configs/${configId}/generateAndCaptureMetrics`);
  }

  downloadConfig(projectId: number, configId: number): Observable<any> {
    return this.http.get<any>(`${this._baseUrl}codeCoverage/project/${projectId}/configs/${configId}/metrics/download`);
  }

  createProjectConfiguration(projectId: number, data: any): Observable<any>{
    return this.http.post<any>(`${this._baseUrl}project/config/${projectId}`, data);
  }

  getBrowserConfiguration(): Observable<any>{
    return this.http.get<any>(`${this._baseUrl}project/config/browsers`)
  }

  getProjectConfiguration(projectId: number): Observable<any>{
    return this.http.get<any>(`${this._baseUrl}project/config/project/${projectId}`)
  }
  getWebAutomationToolConfiguration(): Observable<any>{
    return this.http.get<any>(`${this._baseUrl}project/config/webConfig`)
  }
  updateProjectConfiguration(projectId: number, data: any): Observable<any>{
    return this.http.put<any>(`${this._baseUrl}project/config/${projectId}`, data);
  }

  // Web cluster API starts here
  getNodeHistory(status: any, pageNo: number, pageSize: number) {
    return this.http.get<any>(`${this._baseUrl}webcluster/hub/nodes/requests?status=${status}&pageNo=${pageNo}&pageSize=${pageSize}`);
  }

  getCurrentNodes() {
    return this.http.get<any>(`${this._baseUrl}webcluster/hub/nodes`);
  }

  getInitialisingNodes() {
    return this.http.get<any>(`${this._baseUrl}webcluster/hub/nodes/spinUpQueue`);
  }

  getDrainingNodes() {
    return this.http.get<any>(`${this._baseUrl}webcluster/hub/nodes/spinDownQueue`);
  }

  getCurrentNodeRequest() {
    return this.http.get<any>(`${this._baseUrl}webcluster/hub/nodes/minNodeRequests`);
  }

  requestMinimumNodes(body: any) {
    return this.http.post<any>(`${this._baseUrl}webcluster/sessions/request/minNodes`, body);
  }

  getSessionSummary() {
    return this.http.get<any>(`${this._baseUrl}webcluster/sessions/summary`);
  }

  getSessionList() {
    return this.http.get<any>(`${this._baseUrl}webcluster/sessions/list`);
  }

  getProjectAssociatedList(projectId: number){
    return this.http.get<any>(`${this._baseUrl}endPoints/projects/${projectId}`);
  }

  getGlobalTestInputAssociatedTestCase(globalTestInputId: any, projectId: any) {
    return this.http.get<any>(`${this._baseUrl}globalTestInputKey/${projectId}/${globalTestInputId}`);
  }

  setDefaultPreferredProject(id:any) {
    return this.http.put<ApiResponse>(`${this._baseUrl}projects/defaultProject?projectId=${id}`, {});
  }

  setDefaultPreferredProjectToEmpty() {
    return this.http.put<ApiResponse>(`${this._baseUrl}projects/defaultProject`, {});
  }

  saveGitConfiguration(projectId : any, body: any) {
    return this.http.post<ApiResponse>(`${this._baseUrl}git/project/${projectId}/configs`, body);
  }

  getGitConfigurationList(projectId: any) {
    return this.http.get<any>(`${this._baseUrl}git/project/${projectId}`);
  }

  deleteGitConfiguration(gitConfigurationId: any, projectId: any): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this._baseUrl}git/project/${projectId}/${gitConfigurationId}`);
  }

  getRequiredJiraFields(jiraId : any) {
    return this.http.get<any>(`${this._baseUrl}jira/getMandatoryFields/${jiraId}`);
  }

  getRequiredJiraJSON(projectId: any) {
    return this.http.get<any>(`${this._baseUrl}project/config/${projectId}/jira`);
  }

  saveRequiredJiraJSON(projectId: any, data: any) {
    return this.http.put<any>(`${this._baseUrl}project/config/${projectId}/jira`, data);
  }

  getReviewTestcaseList(projectId: any, body: any) {
    return this.http.post<ApiResponse>(`${this._baseUrl}operations/${projectId}/all`, body);
  }
}
